import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "spectrum-uxp-reference"
    }}>{`Spectrum UXP Reference`}</h1>
    <p>{`UXP supports the UI to be written in three ways using standard HTML Elements, built-in Spectrum UXP widgets, and Spectrum Web Components (recently added)`}</p>
    <p>{`But before we proceed, it's important to understand some terminology`}</p>
    <ul>
      <li parentName="ul">{`Spectrum - An open-source design language and guidelines provided by Adobe.`}</li>
      <li parentName="ul">{`Web components - HTML5 Web technology lets you define custom HTML tags.`}</li>
      <li parentName="ul">{`Adobe Spectrum Web Components (popularly known as SWC) - An open-source library that has Spectrum-styled web components.`}</li>
    </ul>
    <p>{`With that in mind, let's get into the details:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`HTML elements`}</strong>{` `}<br></br>{`
These are pure HTML elements following the web standards. For example, `}<inlineCode parentName="p">{`<img>`}</inlineCode>{`, `}<inlineCode parentName="p">{`<dialog>`}</inlineCode>{` etc. You have the flexibility to style them with your custom stylesheet. Check out the list of supported HTML `}<a parentName="p" {...{
            "href": "../reference-html/"
          }}>{`tags`}</a>{` and `}<a parentName="p" {...{
            "href": "../reference-js/Global%20Members/HTML%20Elements/"
          }}>{`elements`}</a>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Spectrum UXP widgets`}</strong>{` `}<br></br>{`
These are built-in Spectrum-styled UXP components. These widgets mimic the Adobe SWC library APIs and expose a subset of the functionality of the corresponding SWC. `}</p>
        <p parentName="li">{` You can use them as you would any other HTML tag. For example,`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-html"
          }}>{`<sp-button variant="primary">I'm a Spectrum button</sp-button>
`}</code></pre>
        <p parentName="li">{` Take a look at the list of UXP-supported Spectrum widgets - `}<a parentName="p" {...{
            "href": "./Spectrum%20UXP%20Widgets/User%20Interface/"
          }}>{`User Interface`}</a>{` and `}<a parentName="p" {...{
            "href": "./Spectrum%20UXP%20Widgets/Typography/"
          }}>{`Typography`}</a></p>
      </li>
    </ul>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`UXP Spectrum web components`}</strong>{` `}<br></br>{`
These are the Adobe Spectrum Web Components, supported from UXP v7.0 onwards. It's built using Web Components technology.`}</p>
        <p parentName="li">{` In order to use them, you are required to install the components individually and then `}<inlineCode parentName="p">{`import`}</inlineCode>{` them before using the component tag. For example,`}</p>
        <pre parentName="li"><code parentName="pre" {...{}}>{`npm i @swc-uxp-wrappers/button
`}</code></pre>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-js"
          }}>{`import '@swc-uxp-wrappers/button/sp-button.js';
`}</code></pre>
      </li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<sp-button variant="primary">I'm a SWC button</sp-button>
`}</code></pre>
    <p>{`  Note that although the tag appears to be identical to Spectrum UXP widgets, the way they are implemented is fundamentally different. Read on to learn more about the differences.`}</p>
    <h2 {...{
      "id": "differences"
    }}>{`Differences`}</h2>
    <p>{`Now that you know the various options, you must understand their differences and limitations`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`HTML elements`}</strong>{` `}<br></br>{`
Keep in mind that UXP is not a browser. It's a platform that provides the ease of using web technologies to build plugins/scripts for desktop applications. Hence, it does not support all the HTML/CSS capabilities you can use in a browser. In fact, the tags that are `}<em parentName="p">{`not`}</em>{` supported are treated like simple `}<inlineCode parentName="p">{`<div>`}</inlineCode>{`.`}</p>
        <p parentName="li">{` Do take a look at the list of HTML `}<a parentName="p" {...{
            "href": "../reference-html/General/Unsupported%20Elements/"
          }}>{`elements`}</a>{`, and `}<a parentName="p" {...{
            "href": "../reference-html/General/Unsupported%20Attributes/"
          }}>{`attributes`}</a>{` that are `}<em parentName="p">{`not`}</em>{` supported.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Spectrum UXP widgets`}</strong><br></br>{`
Remember that these are built-in within UXP. Therefore, they may appear to be a black-box solution that does not allow you to peek into the details during debugging or alter their behavior easily.`}</p>
        <p parentName="li">{` `}<img alt="Debug Spectrum UXP Widgets" src={require("./assets/debug-sp.png")} /></p>
      </li>
    </ul>
    <ul>
      <li parentName="ul"><strong parentName="li">{`UXP Spectrum web components`}</strong>{` `}<br></br>{`
The support for SWC in UXP is more extensive. It has 30 components including some unique ones that are not available in Spectrum widgets such as table, toast, card etc.
Plus, these components follow the principles of SWC and web components, which means`}
        <ol parentName="li">
          <li parentName="ol">{`You need to `}<inlineCode parentName="li">{`install`}</inlineCode>{` and `}<inlineCode parentName="li">{`import`}</inlineCode>{` individual comps before using them`}</li>
          <li parentName="ol">{`You can debug SWC like a regular Web Component
`}<img alt="Debug SWC" src={require("./assets/debug-swc.png")} /></li>
        </ol>
      </li>
    </ul>
    <p>{`That said, you can mix and match these three offerings to suit your use case. For example, the following will work seamlessly`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-HTML"
      }}>{`<form> <!-- plain HTML element -->
   <sp-banner> <!-- Spectrum Web Component -->
     <div slot="header">Header text</div>
     <div slot="content">Content of the banner</div>
  </sp-banner>
  <sp-button variant="primary">I'm a button</sp-button> <!-- Spectrum widget -->
</form>
`}</code></pre>
    <h2 {...{
      "id": "whats-best-for-you"
    }}>{`What's best for you?`}</h2>
    <p>{`The choice you make depends on your use case and the stage of development you are in.`}</p>
    <p>{`If you are looking for a ready-made Adobe Spectrum-styled UI or are just getting started with programming your UI, we recommend you start your journey with SWC. Fall back to Spectrum UXP widgets for variants in the comps that are not available yet. And use plain HTML elements when they are not available in either.`}</p>
    <p>{`However, if you prefer vanilla HTML and don't mind undertaking the efforts of styling, use the HTML elements along with your custom stylesheet. In fact, you can build your own custom tags using Web Components.`}</p>
    <p>{`If you are thinking of upgrading your existing UI, you should check out the new components in SWC that are not available with Spectrum UXP widgets.`}</p>
    <p>{`Visit the following topics to learn about them in detail`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "swc/index.md"
        }}>{`Spectrum Web Components`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "Spectrum%20UXP%20Widgets/index.md"
        }}>{`Spectrum UXP widgets`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "Spectrum%20to%20SWC%20Mapping/index.md"
        }}>{`Spectrum widgets to SWC Mapping`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      